/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, union, void as voidType } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";
import { ManageDataResultCode } from "./manage-data-result-code.js";

export type ManageDataResultWire =
  | { code: 0 }
  | { code: -1 }
  | { code: -2 }
  | { code: -3 }
  | { code: -4 };

export type ManageDataResultVariantName =
  | "manageDataSuccess"
  | "manageDataNotSupportedYet"
  | "manageDataNameNotFound"
  | "manageDataLowReserve"
  | "manageDataInvalidName";

/**
 * ```xdr
 * union ManageDataResult switch (ManageDataResultCode code)
 * {
 * case MANAGE_DATA_SUCCESS:
 *     void;
 * case MANAGE_DATA_NOT_SUPPORTED_YET:
 * case MANAGE_DATA_NAME_NOT_FOUND:
 * case MANAGE_DATA_LOW_RESERVE:
 * case MANAGE_DATA_INVALID_NAME:
 *     void;
 * };
 * ```
 */
abstract class ManageDataResultBase extends XdrValue {
  abstract readonly type: ManageDataResultVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === ManageDataResultBase) {
      throw new TypeError(
        "new xdr.ManageDataResult(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.ManageDataResult.manageDataSuccess() " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<ManageDataResultWire> = union(
    "ManageDataResult",
    {
      switchOn: ManageDataResultCode.schema,
      cases: [
        case_("manageDataSuccess", 0, voidType()),
        case_("manageDataNotSupportedYet", -1, voidType()),
        case_("manageDataNameNotFound", -2, voidType()),
        case_("manageDataLowReserve", -3, voidType()),
        case_("manageDataInvalidName", -4, voidType()),
      ],
      switchKey: "code",
    },
  );

  static manageDataSuccess(): ManageDataResultSuccess {
    return new ManageDataResultSuccess();
  }

  static manageDataNotSupportedYet(): ManageDataResultNotSupportedYet {
    return new ManageDataResultNotSupportedYet();
  }

  static manageDataNameNotFound(): ManageDataResultNameNotFound {
    return new ManageDataResultNameNotFound();
  }

  static manageDataLowReserve(): ManageDataResultLowReserve {
    return new ManageDataResultLowReserve();
  }

  static manageDataInvalidName(): ManageDataResultInvalidName {
    return new ManageDataResultInvalidName();
  }

  static fromXdrObject(wire: ManageDataResultWire): ManageDataResult {
    switch (wire.code) {
      case 0:
        return new ManageDataResultSuccess();
      case -1:
        return new ManageDataResultNotSupportedYet();
      case -2:
        return new ManageDataResultNameNotFound();
      case -3:
        return new ManageDataResultLowReserve();
      case -4:
        return new ManageDataResultInvalidName();
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `ManageDataResult: unknown code ${(wire as { code: unknown }).code}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete ManageDataResult variant.
   * Use this instead of `instanceof ManageDataResult`: the exported `ManageDataResult` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `ManageDataResult.is(x)` narrows to the union.
   */
  static is(value: unknown): value is ManageDataResult {
    return value instanceof ManageDataResultBase;
  }

  abstract toXdrObject(): ManageDataResultWire;
}

export class ManageDataResultSuccess extends ManageDataResultBase {
  readonly type = "manageDataSuccess" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<ManageDataResultWire, { code: 0 }> {
    return { code: 0 };
  }
}

export class ManageDataResultNotSupportedYet extends ManageDataResultBase {
  readonly type = "manageDataNotSupportedYet" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<ManageDataResultWire, { code: -1 }> {
    return { code: -1 };
  }
}

export class ManageDataResultNameNotFound extends ManageDataResultBase {
  readonly type = "manageDataNameNotFound" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<ManageDataResultWire, { code: -2 }> {
    return { code: -2 };
  }
}

export class ManageDataResultLowReserve extends ManageDataResultBase {
  readonly type = "manageDataLowReserve" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<ManageDataResultWire, { code: -3 }> {
    return { code: -3 };
  }
}

export class ManageDataResultInvalidName extends ManageDataResultBase {
  readonly type = "manageDataInvalidName" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<ManageDataResultWire, { code: -4 }> {
    return { code: -4 };
  }
}

export type ManageDataResult =
  | ManageDataResultSuccess
  | ManageDataResultNotSupportedYet
  | ManageDataResultNameNotFound
  | ManageDataResultLowReserve
  | ManageDataResultInvalidName;
export const ManageDataResult = ManageDataResultBase;
